import cookies from 'js-cookie';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import URI from 'urijs';
import { useAppContext } from 'Context/ReactAppContext';
import type { PayingHousehold } from 'Components/widgets/payment-info/PaymentInfo';
import 'Components/widgets/payment-info/PaymentInfo';
import {
  getTrackPaymentFunc,
  getPaymentErrorMessagePath,
  mapTransportResume,
} from 'Components/widgets/payment-info/PaymentInfo.utils';
import { sanitizePaymentProviders } from 'Components/widgets/payment-info/payment-form/paymentProvidersUtils';
import { getPaymentProviders } from 'Store/booking/booking.selector';
import { fetchSelectedProposalPayment } from 'Store/booking/proposals/proposals.action';
import { BE_TRACKING } from 'Tracking/constants';
import * as trackingStorageApi from 'Tracking/tracking-storage-api';
import { useTracking } from 'Tracking/tracking-api/useTracking';
import { getTrackingRoomType } from 'Tracking/utils';
import {
  BOOKING_SELECTED_RESORT_ID,
  BOOKING_QUERY_PARAMS,
  BOOKING_ENTRY_ROUTE,
  BE_GA_DEFAULT_PAYLOAD,
} from 'Views/booking/constants';
import { PAYMENT_STATUS_REFUSED_PSP } from 'Utils/payment/constants';
import { storeAbTastyEvent } from 'Components/utils/ab-tasty/abTasty.utils';
import { asGqlData, queryParamAsString } from 'Src/types/view-helpers';
import { getPaymentRedirectMutation } from './BankingView.utils';
type Props = {
  resort: ProductWithAccommodations;
  selectedProposal: Proposal;
  additionalProviders?: Array<PaymentProvider>;
  attendeesPaymentStatus: Array<AttendeePaymentStatus>;
};
export const useBankingView = ({
  selectedProposal,
  additionalProviders = [],
  resort,
  attendeesPaymentStatus,
}: Props) => {
  const appContext = useAppContext();
  const router = useRouter();
  const { query: queryParams } = router;
  const { paymentStatus, bookingId } = queryParams;
  const [openFailedPaymentModal, setOpenFailedPaymentModal] = useState(false);
  const firstUnpaidHouseholdIndex = attendeesPaymentStatus.findIndex((status) => !status?.hasPaid);
  const [currentPayingHouseholdIndex, setCurrentPayingHouseholdIndex] = useState(0);
  useEffect(() => {
    if (firstUnpaidHouseholdIndex !== -1) {
      setCurrentPayingHouseholdIndex(firstUnpaidHouseholdIndex);
    }
  }, [firstUnpaidHouseholdIndex]);
  const dispatch = useDispatch();
  const entryRoute = useSelector((state) => state.booking.entryRoute);
  const paymentProviders = useSelector(getPaymentProviders).reduce((allProviders, provider) => {
    if (!provider.shouldSkipPayment) {
      provider.selectorId = provider.id;
    }

    allProviders.push(provider);
    return allProviders;
  }, []);
  const track = useTracking({ ...BE_GA_DEFAULT_PAYLOAD, actionGA: 'banking' });
  const arrivalDate =
    selectedProposal.transports && selectedProposal.transports.inbound
      ? selectedProposal.transports.inbound.travelSections[0].departure.date
      : selectedProposal.arrivalDate;
  const paymentProvidersWithValidMethods = sanitizePaymentProviders({
    paymentProviders: [...paymentProviders, ...additionalProviders],
    arrivalDate,
    locale: appContext.locale,
  });

  const handleFailedPaymentModalClose = () => {
    const newUri = new URI(router.asPath).removeQuery(['paymentStatus']);
    router.replace(newUri.toString());
    setOpenFailedPaymentModal(false);
  };

  useEffect(() => {
    if (queryParamAsString(paymentStatus)?.toUpperCase() === PAYMENT_STATUS_REFUSED_PSP) {
      setOpenFailedPaymentModal(true);
      track({
        labelGA: 'validate_Popin_PaiementRefusedPSP',
        valueGA: 0,
        nonInteraction: 0,
        versionBooking: 'responsive',
      });
    }
  }, [paymentStatus]);
  const hasAllBasePaymentData =
    paymentProviders.length > 0 &&
    selectedProposal.households.every((household) => household.paymentSchedule);
  useEffect(() => {
    storeAbTastyEvent();
    trackingStorageApi.set({
      ...asGqlData<Record<string, unknown>>(trackingStorageApi.get(BE_TRACKING)),
      roomType: getTrackingRoomType(selectedProposal.accommodations),
      insuranceSubscribed: selectedProposal.additionalServices.insurance?.some(
        ({ pricingInfo }) => pricingInfo.isPartiallyRented,
      ),
    });
    const priceHasChanged =
      selectedProposal.price.total.value !==
      selectedProposal.households[currentPayingHouseholdIndex]?.paymentSchedule?.total.value;

    // check for updates in price due to back browser button => re-Fetch the new selectedProposal Price
    // also prevent from double call in refresh for ex
    if (!hasAllBasePaymentData || priceHasChanged) {
      dispatch(fetchSelectedProposalPayment(selectedProposal, appContext));
    }
  }, []);
  const bookingResume = {
    proposalId: selectedProposal.id,
    arrivalDate: selectedProposal.departureDate,
    departureDate: selectedProposal.arrivalDate,
    name: resort.name,
    accommodations: {
      count: selectedProposal.accommodationsCount,
      name: selectedProposal.accommodations[0].category,
    },
    transports: mapTransportResume(selectedProposal.transports),
    attendees: selectedProposal.attendees.list.map(
      ({ customerId }) => customerId || '',
    ) as Array<string>,
  };
  const paymentInfoValue = useMemo(
    () => ({
      trackPayment: getTrackPaymentFunc({
        proposal: selectedProposal,
        track,
        donationAmount: selectedProposal.price.donation?.value || 0,
      }),
      paymentRedirectMutation: getPaymentRedirectMutation({
        proposal: selectedProposal,
        appContext,
        params: {
          bookingId,
        },
      }),
      bookingResume,
      currency: selectedProposal.price.currency,
      donationAmount: selectedProposal.price.donation?.value || '',
      getPaymentErrorMessagePath,
      onPaymentProgress: (_location: URI) => {
        // EOgone psp cannot handle long urls (200 char max),
        // we save proposal id as cookie for the confirmation page
        // see buildRedirectionBE
        cookies.set(BOOKING_SELECTED_RESORT_ID, selectedProposal.id, {
          sameSite: 'none',
          secure: true,
        });
        // The cancelUrl can be too long for the psp so we store in cookies for rehydrate if the user came back #8641
        cookies.set(BOOKING_QUERY_PARAMS, _location.search(), {
          sameSite: 'none',
          secure: true,
        });
        // set the enryRoute in case we came from the comparator to use in confirmation page
        cookies.set(BOOKING_ENTRY_ROUTE, entryRoute);
      },
    }),
    [selectedProposal],
  );
  const household = selectedProposal.households[0];
  const payingHousehold: PayingHousehold = {
    paymentSchedule: household.paymentSchedule,
    customerId: household.attendees[0].customerId,
  };
  const hasBasePaymentData = household.paymentSchedule && paymentProviders.length > 0;
  const householdPaymentStatus = attendeesPaymentStatus.find(
    (bookingAttendee) => bookingAttendee.customerId === household.attendees[0].customerId,
  );
  const hasPaid = householdPaymentStatus?.hasPaid || false;
  return {
    openFailedPaymentModal,
    handleFailedPaymentModalClose,
    paymentInfoValue,
    hasBasePaymentData,
    hasPaid,
    paymentProvidersWithValidMethods,
    paymentProviders,
    household,
    payingHousehold,
  };
};
